import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { deleteServiceByID, Service } from "src/apis/services";
import { getPacientes, Paciente } from "src/apis/prontuario";
import NoDataAlert from "src/pages/components/NoDataAlert";
import { formatDateToBR } from "src/utils/date";
import TableActionsCell from "./TableActionsCell";
import { isUserAdministrator } from "src/utils/auth";
import { Id } from "src/utils/types";
import ServicePaidCheckCell from "./ServicePaidCheckCell";
import ProntuarioDrawer from "./prontuario/ProntuarioDrawer";

type ServicesTableProps = {
  services: Service[],
  hideConfidentialFields?: boolean,
  setError?: (err: string) => void;
}

const ServicesTable: FC<ServicesTableProps> = ({
  services,
  hideConfidentialFields = false,
  setError = (ignored: string) => {}
}): JSX.Element => {
  const hasServices = useMemo(() => Boolean(services) && services.length > 0, [services]);
  const isAdmin = useMemo<boolean>(isUserAdministrator, []);

  const [entries, setEntries] = useState(services);
  const [selectedProntuarioCollaboratorId, setSelectedProntuarioCollaboratorId] =
    useState<Id>(undefined);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const removeEntry = (entryId: Id) => {
    const newEntries = entries?.filter((s) => s.id_atendimento !== entryId);

    setEntries(newEntries)
  }

  useEffect(() => {
    if (hasServices) {
      setEntries(services)
    }
  }, [hasServices, services])

  const formatValue = (value?: string | null): string => {
    if (value === null || value === undefined) {
      return "-";
    }

    const normalizedValue = String(value).trim();

    if (!normalizedValue || normalizedValue.toLowerCase() === "null") {
      return "-";
    }

    return normalizedValue;
  };

  const normalizeText = (value?: string | null): string =>
    String(value ?? "")
      .trim()
      .toLocaleLowerCase("pt-BR")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const findServicePaciente = async (service: Service): Promise<Paciente | undefined> => {
    const patientName = formatValue(service.colaborador);
    const companyName = formatValue(service.empresa);
    const filters = {
      q: patientName !== "-" ? patientName : "",
      empresa: companyName !== "-" ? companyName : "",
    };

    const pacientes = await getPacientes(filters);
    const normalizedServicePatientName = normalizeText(service.colaborador);
    const normalizedServiceCompanyName = normalizeText(service.empresa);

    return (
      pacientes.find((paciente) => paciente.ultimo_atendimento_id === service.id_atendimento) ??
      pacientes.find((paciente) =>
        paciente.id_empresa === service.id_empresa &&
        normalizeText(paciente.nome) === normalizedServicePatientName
      ) ??
      pacientes.find((paciente) =>
        normalizeText(paciente.nome) === normalizedServicePatientName &&
        normalizeText(paciente.empresa_nome) === normalizedServiceCompanyName
      ) ??
      pacientes.find((paciente) => normalizeText(paciente.nome) === normalizedServicePatientName) ??
      (pacientes.length === 1 ? pacientes[0] : undefined)
    );
  };

  const handleOpenProntuario = async (service: Service): Promise<void> => {
    if (service.id_colaborador) {
      setSelectedProntuarioCollaboratorId(service.id_colaborador);
      return;
    }

    try {
      const paciente = await findServicePaciente(service);

      if (!paciente?.id_colaborador) {
        setError("Não foi possível identificar o colaborador desse atendimento.");
        return;
      }

      setEntries((currentEntries) =>
        currentEntries?.map((entry) =>
          entry.id_atendimento === service.id_atendimento
            ? { ...entry, id_colaborador: paciente.id_colaborador }
            : entry
        )
      );
      setSelectedProntuarioCollaboratorId(paciente.id_colaborador);
      setError("");
    } catch (err: any) {
      setError(err?.message ?? "Não foi possível abrir o prontuário.");
    }
  };

  const renderProntuarioShortcut = (service: Service) => (
    <Tooltip title="Abrir prontuário">
      <IconButton
        size="small"
        aria-label="Abrir prontuário"
        onClick={(event) => {
          event.stopPropagation();
          handleOpenProntuario(service);
        }}
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          "&:hover": {
            bgcolor: "primary.dark",
          },
        }}
      >
        <MedicalServicesIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderDetailItem = (label: string, value?: string | number | boolean | null) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">
        {typeof value === "boolean"
          ? value ? "Sim" : "Não"
          : formatValue(value === undefined || value === null ? undefined : String(value))}
      </Typography>
    </Box>
  );

  return (
    <>
      {hasServices ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell><strong>Data de atendimento</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Colaborador</strong></TableCell>
                <TableCell><strong>Psicólogo</strong></TableCell>
                <TableCell><strong>Empresa</strong></TableCell>
                <TableCell><strong>Cidade</strong></TableCell>
                {!hideConfidentialFields && (
                  <>
                    <TableCell><strong>Observações</strong></TableCell>
                    <TableCell><strong>Pago?</strong></TableCell>
                  </>
                )}
                <TableCell><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries?.map((service: Service) => (
                <TableRow
                  hover
                  key={service.id_atendimento}
                  onClick={() => setSelectedService(service)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{renderProntuarioShortcut(service)}</TableCell>
                  <TableCell>{formatDateToBR(service.atendimento)}</TableCell>
                  <TableCell>{formatValue(service.service_status || 'Realizado')}</TableCell>
                  <TableCell>{formatValue(service.colaborador)}</TableCell>
                  <TableCell>{formatValue(service.psicologo)}</TableCell>
                  <TableCell>{formatValue(service.empresa)}</TableCell>
                  <TableCell>{formatValue(service.cidade)}</TableCell>
                  {!hideConfidentialFields && (
                    <>
                      <TableCell>{formatValue(service.observacoes)}</TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <ServicePaidCheckCell
                          isAdmin={isAdmin}
                          service={service}
                          setError={setError}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableActionsCell
                    editUrl={`/atendimentos/${service.id_atendimento}/editar`}
                    showDelete={isAdmin}
                    deleteCallback={async () => {
                        if (window.confirm(`Deseja mesmo deletar esse atendimento (ID ${service.id_atendimento})? Essa ação não poderá ser desfeita`)) {
                          let reason = '';
                          while (String(reason).trim()?.length === 0) {
                            reason = window.prompt('Por qual motivo deseja deletar esse atendimento?') ?? '';
                          }

                          await deleteServiceByID(0, service.id_atendimento, reason);

                          removeEntry(service.id_atendimento);
                        }
                    }}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoDataAlert />
      )}

      <ProntuarioDrawer
        idColaborador={selectedProntuarioCollaboratorId}
        open={Boolean(selectedProntuarioCollaboratorId)}
        onClose={() => setSelectedProntuarioCollaboratorId(undefined)}
        presentation="dialog"
      />

      <Dialog
        open={Boolean(selectedService)}
        onClose={() => setSelectedService(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dados do atendimento e colaborador</DialogTitle>
        <DialogContent>
          {selectedService && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {renderDetailItem("ID do atendimento", selectedService.id_atendimento)}
                {renderDetailItem("Data de atendimento", formatDateToBR(selectedService.atendimento))}
                {renderDetailItem("Status", selectedService.service_status || "Realizado")}
                {renderDetailItem("Pago?", selectedService.pago)}
              </Box>

              <Divider />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {renderDetailItem("Colaborador", selectedService.colaborador)}
                {renderDetailItem("ID do colaborador", selectedService.id_colaborador || "-")}
                {renderDetailItem("Empresa", selectedService.empresa)}
                {renderDetailItem("ID da empresa", selectedService.id_empresa || "-")}
                {renderDetailItem("Área", selectedService.area)}
                {renderDetailItem("Setor", selectedService.setor)}
                {renderDetailItem("Cidade", selectedService.cidade)}
                {renderDetailItem("Psicólogo", selectedService.psicologo)}
              </Box>

              {!hideConfidentialFields && (
                <>
                  <Divider />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {renderDetailItem("Queixa inicial", selectedService.queixa_inicial)}
                    {renderDetailItem("Observações", selectedService.observacoes)}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedService(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ServicesTable;
